import React, { FC, useEffect, useRef, useState } from 'react';

import { Button } from '@chakra-ui/react';
import { MdFileUpload } from 'react-icons/md';
import { Center, Text, Flex, Box } from '@chakra-ui/layout';

import { washHub } from 'store/slices';

import { useMutation, useQuery } from 'react-query';
import { exportPackagesCsv, fetchPackages } from 'api/packages';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';

import { useWashHubListener } from 'hooks';

import { CSVLink } from 'react-csv';

import { Filters } from 'components/primitives/Filters';
import { DataTable } from 'components/modules/DataTable';
import { StatCards } from 'components/primitives/StatCards';

import { PackagesCreatedViewProps } from './PackagesCreated.props';
import { DateRangeInputExt } from 'components/modules/DateRangeInputExt';
import { PackageRow, packagesCreatedColumns } from './packagesCreatedColumns';

export const PackagesCreatedTable: FC<PackagesCreatedViewProps> = (props) => {
  const csvRef = useRef<any>(null);

  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [venue, setVenue] = useState<string[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [status, setStatus] = useState<number[]>();
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const queryUrl = useQueryParams();

  const selectedWashHub = washHub.selectors.useSelectedWashHub();
  const washHubId = selectedWashHub?._id || '';
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const queryKey = [
    `fetch-all-packages-${washHubId}`,
    page,
    sku,
    venue,
    startDate,
    endDate,
    status,
  ];
  const queryFn = () =>
    fetchPackages({
      sku,
      venue,
      page,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
      status,
    });

  const options = {
    enabled: isWashHubIdValid,
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
  };

  useEffect(() => {
    const urlParams = {
      washhubId: String(washHubId),
      ...(props.compType ? { compType: props.compType } : {}),
      ...(type ? { type } : {}),
      ...(venue && venue.length ? { venue: venue.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, props.compType, status, venue, sku, type, endDate, startDate]);

  useEffect(() => {
    const { status, venue, sku, type, startDate, endDate } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    venue && setVenue(venue.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const { isDeleting, onDelete } = props;

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  const { mutate, data: csvData = '', isLoading: isExporting } = useMutation(
    async () =>
      exportPackagesCsv({
        washHub: washHubId,
        sku,
        venue,
        endDate,
        startDate,
        status,
      }),
    {
      onSuccess: () => {
        setTimeout(() => {
          csvRef.current?.link?.click?.();
        }, 500);
      },
    },
  );

  useWashHubListener(() => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
    setStatus(undefined);
  });

  const items = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') setSku(data);
    if (type === 'site') setVenue(data);
  };

  const onClearFilters = () => {
    setPage(1);

    setSku(undefined);
    setVenue(undefined);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  if (!isUrlMapped) return null;

  return (
    <>
      <Flex align="center">
        <DateRangeInputExt
          startDate={startDate}
          endDate={endDate}
          onSearchClear={onSearchClear}
          onSearchPress={onSearchPress}
        />
        <Button
          ml={3}
          minW="200px"
          onClick={() => {
            mutate();
          }}>
          <MdFileUpload fontSize={'1.2rem'} />
          Export Info
        </Button>
        {!isExporting && Boolean(data) && (
          <CSVLink
            data={csvData}
            ref={csvRef}
            filename={`washhub-${selectedWashHub?.name}-packages.csv`}
            target="_blank"
          />
        )}
      </Flex>
      <Box pt={0}>
        <StatCards
          hideValue
          type="packages"
          selected={status}
          onSelectStatus={(_selected) => {
            if (Array.isArray(_selected)) {
              pagination.setPage(1);
              setStatus(_selected);
            }
          }}
        />
      </Box>
      <Filters
        venue={type ? (type == 'sku' ? sku : venue) : []}
        isFullButton
        onApplyFilters={onApplyFilters}
        onClearFilters={onClearFilters}
      />
      <DataTable<PackageRow>
        minW="1050px"
        data={items}
        columns={packagesCreatedColumns}
        isLoading={isLoading || isFetching}
        pagination={pagination}
        renderEmpty={() => (
          <Center p={8}>
            <Text fontSize="20px">No Items available</Text>
          </Center>
        )}
      />
    </>
  );
};
